import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../hooks/useAppStore';
import { track } from './analytics';
import { esRutaPublica as isPublicPath } from './rutasPublicas';

/** Mapping de paths-CTA → action descriptiva.
 *  Cuando un visitante aterriza en una de estas rutas, emitimos un evento
 *  `cta_click` para medir intención de acción (más allá del page_view). */
const CTA_PATHS: Record<string, string> = {
  '/tasar':         'tasar_propiedad',
  '/precalificar':  'precalificar_bbva',
  '/sumate':        'sumate_equipo',
  '/contacto':      'contacto',
};

/**
 * Componente sin render que trackea page_views automáticamente cada vez
 * que cambia la ruta. Además emite cta_click cuando el path es CTA conocido.
 * Decide si la página es del CRM (agent) o pública (client) según el pathname.
 *
 * Se monta una sola vez en App, cerca del RouterProvider.
 */
export function AnalyticsTracker() {
  const { pathname, search } = useLocation();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;

    const isCrmPath = isAuthenticated && !isPublicPath(pathname);
    const category: 'agent' | 'client' = isCrmPath ? 'agent' : 'client';

    track({
      type:     'page_view',
      category,
      action:   'view',
      path:     pathname + (search || ''),
    });

    // CTA click implícito: si el visitante llegó a una página-CTA, emitimos también
    // cta_click. Esto ahorra tener que hookear cada botón individual del sitio.
    const ctaAction = CTA_PATHS[pathname];
    if (ctaAction && category === 'client') {
      track({
        type:     'cta_click',
        category: 'client',
        action:   ctaAction,
        path:     pathname,
      });
    }
  }, [pathname, search, isAuthenticated]);

  return null;
}
